import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { useCallback, useEffect, useState, type ReactNode } from 'react'

import { useAuth } from '../AuthContext'
import { ApiError } from '../api/client'
import {
  COORDINATION_STATUSES,
  QUEUE_KEY,
  SWITCH_QUERY_KEY,
  fetchOnDutySwitch,
  fetchQueue,
  setOnDutySwitch,
  statusLabel,
  type OperationsSwitch,
  type QueueFilters,
} from '../api/emergencyCoordination'
import { EmptyState } from '../components/EmptyState'
import { FilterTabs } from '../components/FilterTabs'
import { ListDisclosure } from '../components/ListDisclosure'
import { StatusPill } from '../components/StatusPill'
import {
  DEFAULT_ON_HOURS,
  REFETCH_MS,
  ageLabel,
  isWaiting,
  toLocalInput,
} from '../coordinationTime'
import { CoordinationRequestDrawer } from './CoordinationRequestDrawer'

/**
 * The coordination workspace (M36-T07, ADR-0065, spec §C.2 and §F.1).
 *
 * The screen a coordinator keeps open while on duty. Until SMS alerts are live
 * it is also how a new request gets NOTICED, which is why it re-fetches every
 * 30 seconds, counts unacknowledged requests in the tab title, and turns a
 * waiting request red, with the word "Waiting", never by colour alone.
 *
 * Every threshold comes from the API (`ack_warning_minutes`,
 * `max_contact_attempts`, `max_on_hours`); the page hard-codes none of them.
 *
 * There is no transport control anywhere here (ADR-0065 decision 5).
 */

const VIEWS = [
  { key: 'all', label: 'All' },
  { key: 'open', label: 'Open' },
  { key: 'feedback', label: 'Feedback list' },
] as const
type View = (typeof VIEWS)[number]['key']

const INPUT =
  'h-9 rounded-[8px] border border-line-2 bg-bg-app px-2 text-sm text-ink-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent'
const BUTTON =
  'inline-flex h-9 items-center justify-center rounded-[8px] border border-line-2 bg-bg-app px-3.5 text-sm font-medium text-ink-1 hover:bg-bg-soft disabled:opacity-50'
const PRIMARY =
  'inline-flex h-9 items-center justify-center rounded-[8px] bg-accent px-3.5 text-sm font-semibold text-accent-on hover:opacity-90 disabled:opacity-50'

/** "18:00", or "Sat 18:00" when the end is not today (a night shift). */
function timeOf(iso: string, now: number): string {
  const d = new Date(iso)
  const time = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  return d.toDateString() === new Date(now).toDateString()
    ? time
    : `${d.toLocaleDateString([], { weekday: 'short' })} ${time}`
}

export function EmergencyCoordinationPage(): ReactNode {
  const { token } = useAuth()
  const [view, setView] = useState<View>('open')
  const [status, setStatus] = useState<string>('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [openId, setOpenId] = useState<string | null>(null)

  const filters: QueueFilters = {
    status: status || null,
    open: view === 'open',
    feedback: view === 'feedback',
    dateFrom,
    dateTo,
  }
  const queue = useQuery({
    queryKey: [...QUEUE_KEY, filters],
    queryFn: () => fetchQueue(token, filters),
    enabled: Boolean(token),
    refetchInterval: REFETCH_MS,
    // Keep counting while the coordinator works in another tab: that is the
    // whole point of the tab-title count.
    refetchIntervalInBackground: true,
    // A filter change keeps the last list (and its count) on screen while the
    // new one loads, so the tab title never drops to zero in between.
    placeholderData: keepPreviousData,
  })

  // The tab title is the alert until SMS is live (spec §C.2). A failed fetch
  // says so in the title too, rather than silently reading as "nothing new".
  const unacknowledged = queue.data?.unacknowledged_total ?? 0
  const failed = queue.isError
  useEffect(() => {
    const previous = document.title
    document.title = failed
      ? '(!) Coordination'
      : unacknowledged > 0
        ? `(${unacknowledged}) Coordination`
        : 'Coordination'
    return () => {
      document.title = previous
    }
  }, [unacknowledged, failed])

  // Stable: the drawer re-runs its focus effect when this changes, and every
  // 30-second refetch re-renders the page. A new function each render moved
  // focus to the drawer's Close button mid-typing (review of M36-T07).
  const closeDrawer = useCallback(() => setOpenId(null), [])

  const items = queue.data?.data ?? []
  const warn = queue.data?.ack_warning_minutes
  // The time of the last fetch, not the clock: it moves with every 30-second
  // refetch, so ages and "Waiting" stay current, and drawing stays pure.
  const now = queue.dataUpdatedAt

  return (
    <section
      className="flex flex-col gap-5"
      aria-labelledby="coordination-heading"
    >
      <div>
        <h1
          id="coordination-heading"
          className="font-display text-2xl font-semibold text-ink-1"
        >
          Emergency coordination
        </h1>
        <p className="mt-1 text-sm text-ink-3">
          Requests from people who asked AMTC to help them reach a hospital.
          Medcor does not dispatch an ambulance. Their first number is always
          112.
        </p>
      </div>

      <OnDutySwitch />

      <div className="flex flex-col gap-3">
        <FilterTabs
          tabs={VIEWS}
          active={view}
          onChange={setView}
          label="Which requests to show"
        />
        <div className="flex flex-wrap items-end gap-3">
          <label className="flex flex-col gap-1 text-xs font-medium text-ink-2">
            Status
            <select
              className={INPUT}
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              <option value="">Any status</option>
              {COORDINATION_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {statusLabel(s)}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-xs font-medium text-ink-2">
            From
            <input
              type="date"
              className={INPUT}
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
            />
          </label>
          <label className="flex flex-col gap-1 text-xs font-medium text-ink-2">
            To
            <input
              type="date"
              className={INPUT}
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
            />
          </label>
        </div>
      </div>

      {queue.isError && queue.data ? (
        // A refetch failed, but the last list is still here: keep it, and say so.
        <p
          role="alert"
          className="rounded-[10px] border border-bed-bad/40 bg-bed-bad-bg px-4 py-2 text-sm font-medium text-bed-bad"
        >
          Could not refresh the list. It may be out of date.{' '}
          <button
            type="button"
            className="underline"
            onClick={() => void queue.refetch()}
          >
            Retry
          </button>
        </p>
      ) : null}

      {queue.isPending ? (
        <div
          role="status"
          aria-label="Loading coordination requests"
          className="h-40 w-full animate-pulse rounded-2xl bg-bg-sunken"
        />
      ) : queue.isError && !queue.data ? (
        <div
          role="alert"
          className="flex items-center justify-between gap-4 rounded-[10px] border border-bed-bad/40 bg-bed-bad-bg px-4 py-3"
        >
          <p className="text-sm font-medium text-bed-bad">
            {queue.error instanceof ApiError
              ? queue.error.message
              : 'Could not load requests.'}
          </p>
          <button
            type="button"
            className={BUTTON}
            onClick={() => void queue.refetch()}
          >
            Retry
          </button>
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border border-line-1">
          <EmptyState title="No requests here.">
            {view === 'feedback'
              ? 'Nobody on this list agreed to be contacted for feedback.'
              : status || dateFrom || dateTo || view !== 'open'
                ? 'No request matches these filters.'
                : 'Nobody is waiting for coordination right now.'}
          </EmptyState>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          <ListDisclosure shown={items.length} total={queue.data?.total} />
          <ul
            className="flex flex-col gap-2"
            aria-label="Coordination requests"
          >
            {items.map((item) => {
              const waiting = warn !== undefined && isWaiting(item, warn, now)
              return (
                <li key={item.id}>
                  <button
                    type="button"
                    onClick={() => setOpenId(item.id)}
                    data-waiting={waiting ? 'true' : undefined}
                    className={`flex w-full flex-wrap items-center gap-x-4 gap-y-1 rounded-[12px] border px-4 py-3 text-left text-sm hover:bg-bg-soft focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent ${
                      waiting
                        ? 'border-bed-bad bg-bed-bad-bg'
                        : 'border-line-1 bg-bg-app'
                    }`}
                  >
                    <span className="font-mono font-semibold text-ink-1">
                      {item.reference}
                    </span>
                    {waiting ? (
                      <span className="rounded-full bg-bed-bad px-2 py-0.5 text-xs font-semibold text-white">
                        Waiting
                      </span>
                    ) : null}
                    <span className="text-ink-3">
                      {ageLabel(item.created_at, now)}
                    </span>
                    <span className="text-ink-2">
                      {item.purged
                        ? 'Details deleted'
                        : (item.callback_phone_masked ?? '—')}
                    </span>
                    <span className="min-w-0 flex-1 truncate text-ink-2">
                      {item.hospital_name ?? 'No hospital chosen'}
                    </span>
                    <StatusPill
                      status={item.status}
                      label={statusLabel(item.status)}
                    />
                  </button>
                </li>
              )
            })}
          </ul>
        </div>
      )}

      <CoordinationRequestDrawer requestId={openId} onClose={closeDrawer} />
    </section>
  )
}

/**
 * "Coordination: ON until 18:00 (set by Ama)" or "Coordination: OFF".
 *
 * Reads `available`, not `is_on`: a switch past its end time reads as off
 * with nobody touching it (ADR-0065 decision 2), and the page says so too.
 */
function OnDutySwitch(): ReactNode {
  const { token } = useAuth()
  const queryClient = useQueryClient()
  const [mode, setMode] = useState<'idle' | 'turning-on' | 'confirm-off'>(
    'idle',
  )
  const [until, setUntil] = useState('')
  const [inlineError, setInlineError] = useState<string | null>(null)

  const sw = useQuery({
    queryKey: SWITCH_QUERY_KEY,
    queryFn: () => fetchOnDutySwitch(token),
    enabled: Boolean(token),
    refetchInterval: REFETCH_MS,
  })

  const save = useMutation({
    mutationFn: (body: Parameters<typeof setOnDutySwitch>[1]) =>
      setOnDutySwitch(token, body),
    onSuccess: (data: OperationsSwitch) => {
      queryClient.setQueryData(SWITCH_QUERY_KEY, data)
      setMode('idle')
    },
  })

  if (sw.isPending) {
    return (
      <div
        role="status"
        aria-label="Loading the on-duty switch"
        className="h-16 rounded-2xl bg-bg-sunken"
      />
    )
  }
  if (sw.isError || !sw.data) {
    return (
      <p role="alert" className="text-sm font-medium text-bed-bad">
        Could not load the on-duty switch.
      </p>
    )
  }
  const data = sw.data
  const on = data.available

  const startTurningOn = () => {
    setUntil(toLocalInput(new Date(Date.now() + DEFAULT_ON_HOURS * 3_600_000)))
    setInlineError(null)
    setMode('turning-on')
  }

  const submitOn = () => {
    const end = new Date(until)
    const now = Date.now()
    if (!until || Number.isNaN(end.getTime())) {
      setInlineError('Choose an end time.')
      return
    }
    if (end.getTime() <= now) {
      setInlineError('The end time must be in the future.')
      return
    }
    if (end.getTime() > now + data.max_on_hours * 3_600_000) {
      setInlineError(
        `Coordination can be on for at most ${data.max_on_hours} hours.`,
      )
      return
    }
    setInlineError(null)
    save.mutate({ on: true, until: end.toISOString() })
  }

  return (
    <div
      className={`flex flex-col gap-3 rounded-2xl border px-4 py-4 ${
        on ? 'border-accent bg-accent-bg' : 'border-line-1 bg-bg-soft'
      }`}
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-ink-1" aria-live="polite">
          <span className="font-semibold">
            Coordination: {on ? 'ON' : 'OFF'}
          </span>
          {on && data.on_until ? (
            <>
              {' '}
              until {timeOf(data.on_until, sw.dataUpdatedAt)}
              {data.changed_by_name ? ` (set by ${data.changed_by_name})` : ''}
            </>
          ) : null}
          <span className="block text-xs text-ink-3">
            {on
              ? 'People on a hospital screen can ask AMTC for help.'
              : 'People see 112 and the hospital directory only.'}
          </span>
        </p>
        {mode === 'idle' ? (
          on ? (
            <button
              type="button"
              className={BUTTON}
              onClick={() => setMode('confirm-off')}
            >
              Turn off
            </button>
          ) : (
            <button type="button" className={PRIMARY} onClick={startTurningOn}>
              Turn on
            </button>
          )
        ) : null}
      </div>

      {mode === 'turning-on' ? (
        <div className="flex flex-wrap items-end gap-3">
          <label className="flex flex-col gap-1 text-xs font-medium text-ink-2">
            Until when?
            <input
              type="datetime-local"
              className={INPUT}
              value={until}
              onChange={(e) => setUntil(e.target.value)}
              aria-describedby="switch-until-help"
            />
          </label>
          <span id="switch-until-help" className="text-xs text-ink-3">
            The end of your shift. At most {data.max_on_hours} hours from now.
            It turns itself off then.
          </span>
          <button
            type="button"
            className={PRIMARY}
            disabled={save.isPending}
            onClick={submitOn}
          >
            {save.isPending ? 'Turning on…' : 'Turn on'}
          </button>
          <button
            type="button"
            className={BUTTON}
            onClick={() => {
              save.reset()
              setMode('idle')
            }}
          >
            Cancel
          </button>
        </div>
      ) : null}

      {mode === 'confirm-off' ? (
        <div
          className="flex flex-wrap items-center gap-3"
          role="group"
          aria-label="Confirm turning off"
        >
          <p className="text-sm text-ink-1">
            Turn coordination off? People will no longer see “Request AMTC
            help”.
          </p>
          <button
            type="button"
            className={PRIMARY}
            disabled={save.isPending}
            onClick={() => save.mutate({ on: false })}
          >
            {save.isPending ? 'Turning off…' : 'Yes, turn off'}
          </button>
          <button
            type="button"
            className={BUTTON}
            onClick={() => {
              save.reset()
              setMode('idle')
            }}
          >
            Keep it on
          </button>
        </div>
      ) : null}

      {inlineError ? (
        <p role="alert" className="text-sm font-medium text-bed-bad">
          {inlineError}
        </p>
      ) : null}
      {save.isError ? (
        <p role="alert" className="text-sm font-medium text-bed-bad">
          {save.error instanceof ApiError
            ? save.error.message
            : 'Could not change the switch.'}
        </p>
      ) : null}
    </div>
  )
}
